import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLang } from '../context/LanguageContext';
import { useWhatsAppHelp } from '../context/WhatsAppHelpContext';
import { openBusinessWhatsApp } from '../config/whatsappSuggestions';
import { isNativeApp } from '../utils/platform.js';
import { goNativeTab } from '../mobile/NativeTabContext';
import '../mobile/NativeInfoSheet.css';
import './HelpGuidePage.css';

const STEPS = [
  { icon: '🌐', titleKey: 'helpStep1Title', bodyKey: 'helpStep1Body' },
  { icon: '📰', titleKey: 'helpStep2Title', bodyKey: 'helpStep2Body' },
  { icon: '🔤', titleKey: 'helpStep3Title', bodyKey: 'helpStep3Body' },
  { icon: '🔐', titleKey: 'helpStep4Title', bodyKey: 'helpStep4Body' },
  { icon: '💬', titleKey: 'helpStep5Title', bodyKey: 'helpStep5Body' },
];

export default function HelpGuidePage({ onClose, onOpenFaq }) {
  const { t } = useLang();
  const { openWhatsAppHelp } = useWhatsAppHelp();

  const scrollToPaper = () => {
    onClose?.();
    if (isNativeApp()) {
      goNativeTab('paper');
      return;
    }
    const target = document.getElementById('paper');
    if (!target) return;
    const header = document.getElementById('site-header');
    const offset = header?.offsetHeight ?? 0;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const openFaq = () => {
    onClose?.();
    onOpenFaq?.();
  };

  const contactWa = () => {
    onClose?.();
    openWhatsAppHelp('help_guide');
  };

  const sendGuideWa = () => {
    openBusinessWhatsApp(t.waSuggest_guide_msg);
    onClose?.();
  };

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const onBack = () => onClose?.();
    window.addEventListener('rk-native-back', onBack);
    return () => window.removeEventListener('rk-native-back', onBack);
  }, [onClose]);

  const body = (
    <>
      <div className="help-guide__hero">
        <span className="help-guide__hero-icon" aria-hidden>
          📖
        </span>
        <h1>{t.helpGuideTitle}</h1>
        <p className="about-tagline">{t.helpGuideSubtitle}</p>
      </div>

      <ol className="help-guide__steps">
        {STEPS.map((step, index) => (
          <li key={step.titleKey} className="help-guide__step">
            <span className="help-guide__step-num">{index + 1}</span>
            <span className="help-guide__step-icon" aria-hidden>
              {step.icon}
            </span>
            <div className="help-guide__step-body">
              <h3>{t[step.titleKey]}</h3>
              <p>{t[step.bodyKey]}</p>
              {index === 1 && (
                <button type="button" className="help-guide__inline-btn" onClick={scrollToPaper}>
                  → {t.navEpaper || t.nav?.[1]}
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>

      <div className="help-guide__actions">
        <button type="button" className="help-guide__action help-guide__action--ghost" onClick={openFaq}>
          {t.helpGuideOpenFaq}
        </button>
        <button type="button" className="help-guide__action" onClick={contactWa}>
          {t.helpGuideCallWa}
        </button>
        <button type="button" className="help-guide__action help-guide__action--secondary" onClick={sendGuideWa}>
          WhatsApp →
        </button>
      </div>
    </>
  );

  if (isNativeApp()) {
    return createPortal(
      <div className="ios-sheet help-guide" role="dialog" aria-modal="true">
        <header className="ios-sheet__bar">
          <button type="button" className="ios-sheet__back" onClick={onClose} aria-label="Back">
            <svg viewBox="0 0 12 20" width="12" height="20" aria-hidden>
              <path d="M10 2L2 10l8 8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1>{t.navHelp}</h1>
          <button type="button" className="ios-sheet__done" onClick={onClose}>
            {t.nativeDone}
          </button>
        </header>
        <div className="ios-sheet__body">
          <p className="ios-support__lead">{t.helpGuideSubtitle}</p>
          <div className="ios-support__group">
            {STEPS.map((step, index) => {
              const inner = (
                <span>
                  <strong>{t[step.titleKey]}</strong>
                  <em>{t[step.bodyKey]}</em>
                </span>
              );
              if (index === 1) {
                return (
                  <button key={step.titleKey} type="button" className="ios-support__row" onClick={scrollToPaper}>
                    {inner}
                    <span className="ios-support__go" aria-hidden />
                  </button>
                );
              }
              return (
                <div key={step.titleKey} className="ios-support__row ios-support__row--static">
                  {inner}
                </div>
              );
            })}
          </div>
          <div className="ios-support__group" style={{ marginTop: 16 }}>
            <button type="button" className="ios-support__row" onClick={openFaq}>
              <span>{t.helpGuideOpenFaq}</span>
              <span className="ios-support__go" aria-hidden />
            </button>
            <button type="button" className="ios-support__row" onClick={contactWa}>
              <span>{t.helpGuideCallWa}</span>
              <span className="ios-support__go" aria-hidden />
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <div className="page-modal-backdrop" onClick={onClose}>
      <div className="page-modal page-modal--wide help-guide" onClick={(e) => e.stopPropagation()}>
        <div className="page-modal-header">
          <h2>{t.helpGuideTitle}</h2>
          <button type="button" className="page-modal-close" onClick={onClose} aria-label={t.closeBtn}>
            ✕
          </button>
        </div>
        <div className="page-modal-body">{body}</div>
      </div>
    </div>
  );
}
